import type { SubLedger, SubLedgerType, SubLedgerSummaryPagedList, SystemYear, AssetLookupText, Supplier, SupplierSummaryPagedList, FinancialsSupplierAddress, FinancialsSupplierAddressPagedList, FinancialsSupplierAddressInput } from '@/types/financials';
import { getAuthConfig } from '@/lib/auth-fetch';

// Mock data for demo mode
const mockSubLedgers: SubLedger[] = [
  { id: '1', code: 'SL001', description: 'General Ledger', type: 'Nominal' },
  { id: '2', code: 'SL002', description: 'Fixed Assets', type: 'Nominal' },
  { id: '3', code: 'SL003', description: 'Inventory', type: 'Nominal' },
  { id: '4', code: 'SL004', description: 'Accounts Payable', type: 'Purchase' },
  { id: '5', code: 'SL005', description: 'Accounts Receivable', type: 'Sales' },
];

const mockSystemYears: SystemYear[] = [
  { id: '1', label: '2025', startDate: '2025-01-01', endDate: '2025-12-31', nominal: 2025 },
  { id: '2', label: '2024', startDate: '2024-01-01', endDate: '2024-12-31', nominal: 2024 },
  { id: '3', label: '2023', startDate: '2023-01-01', endDate: '2023-12-31', nominal: 2023 },
  { id: '4', label: '2022', startDate: '2022-01-01', endDate: '2022-12-31', nominal: 2022 },
  { id: '5', label: '2021', startDate: '2021-01-01', endDate: '2021-12-31', nominal: 2021 },
];

const mockAssetLookupText: Record<string, AssetLookupText[]> = {
  'ASSORT1': [
    { id: '1', origin: 'ASSORT1', text: 'Department A', sortOrder: 1 },
    { id: '2', origin: 'ASSORT1', text: 'Department B', sortOrder: 2 },
    { id: '3', origin: 'ASSORT1', text: 'Department C', sortOrder: 3 },
  ],
  'ASSORT2': [
    { id: '4', origin: 'ASSORT2', text: 'Building 1', sortOrder: 1 },
    { id: '5', origin: 'ASSORT2', text: 'Building 2', sortOrder: 2 },
    { id: '6', origin: 'ASSORT2', text: 'Building 3', sortOrder: 3 },
  ],
  'ASSORT3': [
    { id: '7', origin: 'ASSORT3', text: 'Project Alpha', sortOrder: 1 },
    { id: '8', origin: 'ASSORT3', text: 'Project Beta', sortOrder: 2 },
    { id: '9', origin: 'ASSORT3', text: 'Project Gamma', sortOrder: 3 },
  ],
  'ASSORT4': [
    { id: '10', origin: 'ASSORT4', text: 'Cost Center 100', sortOrder: 1 },
    { id: '11', origin: 'ASSORT4', text: 'Cost Center 200', sortOrder: 2 },
    { id: '12', origin: 'ASSORT4', text: 'Cost Center 300', sortOrder: 3 },
  ],
  'ASSORT5': [
    { id: '13', origin: 'ASSORT5', text: 'Region North', sortOrder: 1 },
    { id: '14', origin: 'ASSORT5', text: 'Region South', sortOrder: 2 },
    { id: '15', origin: 'ASSORT5', text: 'Region East', sortOrder: 3 },
    { id: '16', origin: 'ASSORT5', text: 'Region West', sortOrder: 4 },
  ],
};

const mockSuppliers: Supplier[] = [
  { primaryKey: 1, code: 'SUP001', name: 'Acme Corporation', inactive: false, balance: 15000.00, currencyCode: 'GBP' },
  { primaryKey: 2, code: 'SUP002', name: 'Tech Solutions Ltd', inactive: false, balance: 8500.50, currencyCode: 'GBP' },
  { primaryKey: 3, code: 'SUP003', name: 'Office Supplies Inc', inactive: false, balance: 2300.00, currencyCode: 'GBP' },
  { primaryKey: 4, code: 'SUP004', name: 'Industrial Equipment Co', inactive: false, balance: 45000.00, currencyCode: 'GBP' },
  { primaryKey: 5, code: 'SUP005', name: 'Global Logistics', inactive: true, balance: 0, currencyCode: 'GBP' },
  { primaryKey: 6, code: 'SUP006', name: 'Premium Parts Ltd', inactive: false, balance: 12750.25, currencyCode: 'GBP' },
];

const mockSupplierAddresses: Record<string, FinancialsSupplierAddress[]> = {
  'SUP001': [
    {
      summarySection: { primaryKey: 1, number: 1, accountCode: 'SUP001', isOrder: true, isRemittance: false, isDelivery: true, completeContactName: 'John Smith' },
      details: { address: '123 Main Street', town: 'London', county: 'Greater London', country: 'UK', postCode: 'EC1A 1BB', telephone: '020 1234 5678', email: 'orders@acme.com' },
    },
    {
      summarySection: { primaryKey: 2, number: 2, accountCode: 'SUP001', isOrder: false, isRemittance: true, isDelivery: false, completeContactName: 'Finance Dept' },
      details: { address: '456 Finance Road', town: 'Manchester', county: 'Greater Manchester', country: 'UK', postCode: 'M1 1AA', telephone: '0161 234 5678', email: 'finance@acme.com' },
    },
  ],
  'SUP002': [
    {
      summarySection: { primaryKey: 3, number: 1, accountCode: 'SUP002', isOrder: true, isRemittance: true, isDelivery: true, completeContactName: 'Sarah Johnson' },
      details: { address: '789 Tech Park', town: 'Cambridge', county: 'Cambridgeshire', country: 'UK', postCode: 'CB1 2AB', telephone: '01onal 345 6789', email: 'info@techsolutions.com' },
    },
  ],
  'SUP003': [
    {
      summarySection: { primaryKey: 4, number: 1, accountCode: 'SUP003', isOrder: true, isRemittance: true, isDelivery: true, completeContactName: 'Mike Brown' },
      details: { address: '321 Office Lane', town: 'Birmingham', county: 'West Midlands', country: 'UK', postCode: 'B1 1AA', telephone: '0121 456 7890', email: 'sales@officesupplies.com' },
    },
  ],
  'SUP004': [
    {
      summarySection: { primaryKey: 5, number: 1, accountCode: 'SUP004', isOrder: true, isRemittance: false, isDelivery: true, completeContactName: 'Industrial Sales' },
      details: { address: '555 Industrial Estate', town: 'Sheffield', county: 'South Yorkshire', country: 'UK', postCode: 'S1 1AA', telephone: '0114 567 8901', email: 'orders@industrial.com' },
    },
    {
      summarySection: { primaryKey: 6, number: 2, accountCode: 'SUP004', isOrder: false, isRemittance: true, isDelivery: false, completeContactName: 'Accounts Payable' },
      details: { address: '556 Industrial Estate', town: 'Sheffield', county: 'South Yorkshire', country: 'UK', postCode: 'S1 1AB', telephone: '0114 567 8902', email: 'accounts@industrial.com' },
    },
  ],
  'SUP006': [
    {
      summarySection: { primaryKey: 7, number: 1, accountCode: 'SUP006', isOrder: true, isRemittance: true, isDelivery: true, completeContactName: 'Parts Department' },
      details: { address: '999 Parts Avenue', town: 'Leeds', county: 'West Yorkshire', country: 'UK', postCode: 'LS1 1AA', telephone: '0113 678 9012', email: 'parts@premiumparts.com' },
    },
  ],
};

// Helper function to get current data source mode from localStorage
const getDataSource = (): 'demo' | 'api' => {
  const mode = localStorage.getItem('app-mode');
  return mode === 'live' ? 'api' : 'demo';
};

class FinancialsService {
  private authHeader?: string;

  constructor() { }

  private getApiBaseUrl(): string {
    // Use environment variable for Financials API base URL
    // Falls back to a default if not set
    const baseUrl = import.meta.env.VITE_FINANCIALS_API_BASE_URL || 'http://localhost:50522';

    try {
      const url = new URL(baseUrl);
      if (url.username || url.password) {
        const credentials = btoa(`${url.username}:${url.password}`);
        this.authHeader = `Basic ${credentials}`;
        url.username = '';
        url.password = '';
        return url.toString().replace(/\/$/, '');
      }
      return baseUrl.replace(/\/$/, '');
    } catch (e) {
      return baseUrl.replace(/\/$/, '');
    }
  }

  private async fetchAPI(endpoint: string, options?: RequestInit) {
    const apiBaseUrl = this.getApiBaseUrl();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };

    // Add Basic Auth if extracted from URL
    if (this.authHeader) {
      headers['Authorization'] = this.authHeader;
    }

    // Get auth config for Bearer token and Instance ID
    const authConfig = getAuthConfig();

    // Add Bearer token if available
    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    // Add X-Instance-Id header (required by Financials API)
    const instanceId = authConfig.instanceId || 'default-instance';
    headers['X-Instance-Id'] = instanceId;

    try {
      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        let errorMessage = `Financials API error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // Ignore JSON parse errors
        }
        throw new Error(errorMessage);
      }

      if (response.status === 204) {
        return null;
      }

      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Unable to connect to Financials API at ${apiBaseUrl}. Please ensure the Financials server is running or switch to Demo mode.`);
      }
      throw error;
    }
  }

  async getSubLedgers(type?: SubLedgerType): Promise<SubLedger[]> {
    if (getDataSource() === 'demo') {
      // Filter mock data by type if provided
      if (type) {
        return mockSubLedgers.filter(sl => sl.type === type);
      }
      return mockSubLedgers;
    }

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.set('UsePaging', 'false'); // Fetch all results for dropdown
      if (type) {
        params.set('type', type);
      }

      // Fetch from correct endpoint
      const data = await this.fetchAPI(
        `/config/lists/sub-ledgers?${params.toString()}`
      ) as SubLedgerSummaryPagedList;

      // Extract list and map to SubLedger interface
      const list = data.list ?? [];
      return list
        .filter(sl => sl.code) // Guard against null codes
        .map(sl => ({
          code: sl.code as string,
          description: sl.description,
          auditNo: sl.auditNo,
          creditNo: sl.creditNo,
          invoiceNo: sl.invoiceNo,
          type: sl.type,
        }));
    } catch (error) {
      console.error('Failed to fetch sub ledgers from Financials API:', error);
      throw error;
    }
  }

  async getSystemYears(sort?: string): Promise<SystemYear[]> {
    if (getDataSource() === 'demo') {
      // Return mock data, sorted if requested
      const years = [...mockSystemYears];
      if (sort === 'desc') {
        return years.sort((a, b) => b.nominal - a.nominal);
      } else if (sort === 'asc') {
        return years.sort((a, b) => a.nominal - b.nominal);
      }
      return years;
    }

    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (sort) {
        params.set('sort', sort);
      }

      // Fetch from Settings API endpoint
      const data = await this.fetchAPI(
        `/api/Settings/system-years${params.toString() ? `?${params.toString()}` : ''}`
      ) as SystemYear[];

      return data;
    } catch (error) {
      console.error('Failed to fetch system years from API:', error);
      throw error;
    }
  }

  async getAssetLookupText(origin: string): Promise<AssetLookupText[]> {
    if (getDataSource() === 'demo') {
      // Return mock data for the specified origin
      return mockAssetLookupText[origin] || [];
    }

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.set('origin', origin);

      // Fetch from Settings API endpoint
      const data = await this.fetchAPI(
        `/api/Settings/asset-lookup-text?${params.toString()}`
      ) as AssetLookupText[];

      // Sort by sortOrder
      return data.sort((a, b) => a.sortOrder - b.sortOrder);
    } catch (error) {
      console.error('Failed to fetch asset lookup text from API:', error);
      throw error;
    }
  }

  async getSuppliers(includeInactive?: boolean): Promise<Supplier[]> {
    if (getDataSource() === 'demo') {
      // Filter out inactive suppliers unless explicitly requested
      if (includeInactive) {
        return mockSuppliers;
      }
      return mockSuppliers.filter(s => !s.inactive);
    }

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.set('UsePaging', 'false'); // Fetch all results for dropdown
      if (!includeInactive) {
        params.set('inactive', 'false');
      }

      // Fetch from Financials API endpoint
      const data = await this.fetchAPI(
        `/creditors/lists/suppliers?${params.toString()}`
      ) as SupplierSummaryPagedList;

      // Extract list and return
      const list = data.list ?? [];
      return list.filter(s => s.code); // Guard against null codes
    } catch (error) {
      console.error('Failed to fetch suppliers from Financials API:', error);
      throw error;
    }
  }

  async getSupplierAddresses(supplierCode: string): Promise<FinancialsSupplierAddress[]> {
    if (getDataSource() === 'demo') {
      // Return mock data for the specified supplier
      return mockSupplierAddresses[supplierCode] || [];
    }

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.set('supplierCode', supplierCode);

      // Fetch from Financials API endpoint
      const data = await this.fetchAPI(
        `/creditors/master-records/supplier/addresses?${params.toString()}`
      ) as FinancialsSupplierAddressPagedList;

      // Extract list from paged response
      return data.list ?? [];
    } catch (error) {
      console.error('Failed to fetch supplier addresses from Financials API:', error);
      throw error;
    }
  }

  async createSupplierAddress(supplierCode: string, address: FinancialsSupplierAddressInput): Promise<FinancialsSupplierAddress[]> {
    if (getDataSource() === 'demo') {
      // In demo mode, add to mock data and return updated list
      const newAddress: FinancialsSupplierAddress = {
        summarySection: {
          primaryKey: Date.now(),
          number: (mockSupplierAddresses[supplierCode]?.length || 0) + 1,
          accountCode: supplierCode,
          isOrder: address.summarySection.isOrder,
          isRemittance: address.summarySection.isRemittance,
          isStatement: address.summarySection.isStatement,
          isDelivery: address.summarySection.isDelivery,
          isDeliveryDefault: address.summarySection.isDeliveryDefault,
          completeContactName: [
            address.summarySection.contactDetails?.firstName,
            address.summarySection.contactDetails?.surname
          ].filter(Boolean).join(' ') || undefined,
          contactDetails: address.summarySection.contactDetails ? {
            telephone: address.details?.telephone,
            mobile: address.details?.mobile,
            email: address.details?.email,
            fax: address.details?.fax,
          } : undefined,
        },
        details: address.details,
        additionalDetails: address.additionalDetails,
      };

      if (!mockSupplierAddresses[supplierCode]) {
        mockSupplierAddresses[supplierCode] = [];
      }
      mockSupplierAddresses[supplierCode].push(newAddress);

      return mockSupplierAddresses[supplierCode];
    }

    try {
      // POST to Financials API endpoint
      // The API expects an array of addresses
      const data = await this.fetchAPI(
        `/creditors/master-records/supplier/addresses`,
        {
          method: 'POST',
          body: JSON.stringify([address]),
        }
      ) as FinancialsSupplierAddress[];

      return data;
    } catch (error) {
      console.error('Failed to create supplier address in Financials API:', error);
      throw error;
    }
  }
}

export const financialsService = new FinancialsService();
